﻿import { RESTError } from "../API/RESTError";
import { GetHistoricalForecastResponse } from "./GetHistoricalForecastResponse";
import { InfluxDataWrapper } from "./InfluxDataWrapper";
import { InfluxJsonResponse } from "./InfluxJsonReponse";

function escapeSeriesNamePart(str: string): string
{
    return str.replace(/\./g, "..").replace(/\//g, "_").replace(/ /g, "_");
}

export function getParamSeriesName(store: string, stationId: string, sensorId: string, parameterId: string, variant: string): string
{
    var r = store + "." + escapeSeriesNamePart(stationId) + "." + escapeSeriesNamePart(sensorId) + "." + escapeSeriesNamePart(parameterId);
    if (variant !== null) {
        r += "." + escapeSeriesNamePart(variant);
    }
    return r;
}

export function getGroupSeriesName(stationId: string, parameterGroup: string, sensorId: string = undefined): string
{
    var sn = "G." + escapeSeriesNamePart(stationId) + "." + escapeSeriesNamePart(parameterGroup);
    if (sensorId !== undefined) {
        sn += "." + escapeSeriesNamePart(sensorId);
    }
    return sn;
}

function getInfluxServer(): string {
    return '/api/HistoricalData';
}

function decodeValueFromInflux(str: string): any {
    if (str == null) {
        return null;
    }
    return JSON.parse(str);
}

export function getHistoricForecastData(callback: (err: RESTError, resp: GetHistoricalForecastResponse) => any, store: string, stationId: string, sensorId: string, parameterId: string, issueTime?: number, baseVariant?: string) {
    let str = '/api/v1/HistoricalForecasts/' + encodeURIComponent(store) + "/" + encodeURIComponent(stationId) + "/" + encodeURIComponent(sensorId) + "/" + encodeURIComponent(escapeSeriesNamePart(parameterId));//keep escapeSeriesNamePart here to ensure we keep the slash in parameterId (MESSY!)

    let queryParts: string[] = [];
    if (issueTime !== null) {
        queryParts.push("issueTime=" + issueTime);
    }

    queryParts.push("format=json");//format may also be "csv"
    queryParts.push("output=inline");//output may be "file"

    if (baseVariant !== null) {
        queryParts.push("baseVariant=" + baseVariant);
    }

    if (queryParts.length) {
        str += "/?" + queryParts.join('&');
    }

    return $.ajax(str, {
        cache: false,
        dataType: 'json',
        type: 'GET',
        timeout: 30000,
        success: (resp: GetHistoricalForecastResponse) => {
            callback(null, resp);
        },
        error: (jqXHR: JQueryXHR) => {
            callback(new RESTError(jqXHR), null);
        }
    });
}


export function getHistoricData(store: string, stationId: string, sensorId: string, parameterId: string, variant: string, startDate: number, endDate: number, callback: (err: RESTError, data: InfluxDataWrapper) => any, desiredResolution: number): JQueryXHR {
    var startDateSeconds = startDate / 1000;
    var endDateSeconds = endDate / 1000;

    var startDateSecondsString = (startDate / 1000).toFixed(0);
    var endDateSecondsString = (endDate / 1000).toFixed(0);

    var seriesName = getParamSeriesName(store, stationId, sensorId, parameterId, variant);

    var resolution: number;
    
    //all
    if (desiredResolution == -1) {
        var q = 'SELECT value FROM "midi"."rp"."' + seriesName + '" WHERE time >= ' + startDateSecondsString + 's AND time < ' + endDateSecondsString + 's';
        resolution = 1;
    } else {
        var maxDataPoints = 1440;
        var groupByPeriod: number = 0;

        // assume a full sample of data
        var minutes = (endDateSeconds - startDateSeconds) / 60;
        if (desiredResolution == 0 || minutes > maxDataPoints) {
            groupByPeriod = Math.ceil(minutes / maxDataPoints);
        } else {
            groupByPeriod = desiredResolution;
        }

        q = 'SELECT FIRST(value) FROM "midi"."rp"."' + seriesName + '" WHERE time >= ' + startDateSecondsString + 's AND time < ' + endDateSecondsString + 's GROUP BY time(' + groupByPeriod + 'm)';
        resolution = groupByPeriod;
    }
    
    return $.ajax(getInfluxServer() + '/?q=' + encodeURIComponent(q), {
        async: true,
        cache: false,
        dataType: 'json',
        type: 'GET',
        timeout: 30000,
        success: (data: InfluxJsonResponse, text: string, jqXHR: JQueryXHR) => {
            var responseData: any[] = [];
            let tsFirst: number = null;
            let tsLast: number = null;

            if (data.results[0].series) {
                data.results[0].series.forEach((series: any) => {
                    if (!series.values) {
                        return;
                    }

                    // data can be chunked across multiple series

                    series.values.forEach((value: any[]) => {
                        // used for plotting only - nulls are plotted as gaps in a line chart
                        // group by can return nulls if frequency of data return less than grouping period
                        // all data points which can carry nulls (i.e. cloud height, amount) should be plotted as points anyway
                        // nulls will therfore be removed from data to avoid gaps in low frequency line data

                        if (value[1] == null) {
                            return;
                        }

                        value[0] = moment.utc(value[0], 'YYYY-MM-DD HH:mm:ss').valueOf();
                        value[1] = decodeValueFromInflux(value[1]);

                        if (value[1] == 'NIL') {
                            return;
                        }

                        //: first/last timestamps, to help with auto-refresh of plot data:
                        if (tsFirst == null || tsFirst > value[0]) {
                            tsFirst = value[0];
                        }

                        if (tsLast == null || tsLast < value[0]) {
                            tsLast = value[0];
                        }

                        responseData.push(value);
                    });
                });
            }

            var response: InfluxDataWrapper = {
                Store: store,
                StationId: stationId,
                ParameterId: parameterId,
                ParameterDefinition: null,
                SensorId: sensorId,
                Data: responseData,
                Resolution: resolution,
                TimestampFirst: tsFirst,
                TimestampLast: tsLast
            };

            callback(null, response);
        },
        error: (jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => {
            callback(new RESTError(jqXHR), null);
        }
    });
}

export function getGroupData(stationId: string, parameterGroup: string, startDate: number, endDate: number, callback: (err: any, data: InfluxDataWrapper) => any, sensorId: string = undefined): JQueryXHR {

    var startDateSeconds = (startDate / 1000).toFixed(0);
    var endDateSeconds = (endDate / 1000).toFixed(0);

    var seriesName = getGroupSeriesName(stationId, parameterGroup, sensorId);
    var q = 'SELECT value FROM "midi"."rp"."' + seriesName + '" WHERE time >= ' + startDateSeconds + 's AND time < ' + endDateSeconds + 's';
    
    return $.ajax(getInfluxServer() + '/?q=' + encodeURIComponent(q), {
        cache: false,
        dataType: 'json',
        type: 'GET',
        timeout: 30000,
        success: (data: InfluxJsonResponse, text: string, jqXHR: JQueryXHR) => {

            var responseData: any[] = null; // null if table doesn't exist

            if (data.results[0].series) {

                responseData = []; // empty if table exists, but no data

                data.results[0].series.forEach((series: any) => {

                    if (!series.values) {
                        return;
                    }

                    // data can be chunked across multiple series

                    series.values.forEach((value: any[]) => {

                        // used for plotting only - nulls are plotted as gaps in a line chart
                        // group by can return nulls if frequency of data return less than grouping period
                        // all data points which can carry nulls (i.e. cloud height, amount) should be plotted as points anyway
                        // nulls will therfore be removed from data to avoid gaps in low frequency line data

                        if (value[1] == null) {
                            return;
                        }

                        value[0] = moment.utc(value[0], 'YYYY-MM-DD HH:mm:ss').valueOf();
                        value[1] = decodeValueFromInflux(value[1]);

                        if (value[1] == 'NIL') {
                            return;
                        }

                        responseData.push(value);

                    });
                });

            }

            var response: InfluxDataWrapper = {
                Store: 'G',
                StationId: stationId,
                ParameterId: parameterGroup,
                ParameterDefinition: null,
                SensorId: sensorId,
                Data: responseData
            };

            callback(null, response);

        },
        error: (jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => {
            callback(new RESTError(jqXHR), null);
        }
    });
}